import { useAbility } from "@casl/react";
import { Link, useRouterState } from "@tanstack/react-router";
import { FC } from "react";

import type { AppAbility } from "@recobra/core";
import { m } from "@recobra/i18n/messages";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@recobra/ui/sidebar";

import logoUrl from "../assets/logo.png";
import { defaultAuthedRoute } from "../lib";
import { CaseTaskSidebarItems } from "./CaseTaskSidebarItems";
import { type NavItem, navItems, reportNavItems, settingsNavItems } from "./NavItems";
import { SidebarNavUser } from "./SidebarNavUser";

export const AppSidebar: FC<{
  cognitoGroups: readonly string[];
  userName: string;
  userEmail?: string;
  userAvatar?: string;
  onSignOut: () => void;
}> = ({ cognitoGroups, userName, userEmail, userAvatar, onSignOut }) => {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const homeRoute = defaultAuthedRoute(cognitoGroups);
  const ability = useAbility<AppAbility>();

  const isActive = (to: string) => {
    if (to === "/") return pathname === "/";

    return pathname === to || pathname.startsWith(`${to}/`);
  };

  const isVisible = (item: NavItem) => !item.permission || ability.can(...item.permission);
  const visibleNavItems = navItems.filter(isVisible);
  const visibleReportNavItems = reportNavItems.filter(isVisible);
  const visibleSettingsNavItems = settingsNavItems.filter(isVisible);

  const renderNavItem = (item: NavItem) => (
    <SidebarMenuItem key={item.to}>
      <SidebarMenuButton
        tooltip={item.title()}
        render={<Link to={item.to} />}
        isActive={isActive(item.to)}
      >
        <item.icon />
        <span>{item.title()}</span>
      </SidebarMenuButton>
      {item.to === "/my-tasks" ? <CaseTaskSidebarItems /> : null}
    </SidebarMenuItem>
  );

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton size="lg" tooltip={m.app_name()} render={<Link to={homeRoute} />}>
              <div className="flex aspect-square size-8 items-center justify-center overflow-hidden rounded-lg">
                <img src={logoUrl} alt="Recobra Logo" className="size-8 object-contain" />
              </div>
              <div className="flex flex-col gap-0.5 leading-none group-data-[collapsible=icon]:hidden">
                <span className="font-semibold">{m.app_name()}</span>
              </div>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>{m.nav_application()}</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>{visibleNavItems.map(renderNavItem)}</SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
        {visibleReportNavItems.length ? (
          <SidebarGroup>
            <SidebarGroupLabel>{m.nav_reports()}</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>{visibleReportNavItems.map(renderNavItem)}</SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ) : null}
        {visibleSettingsNavItems.length ? (
          <SidebarGroup>
            <SidebarGroupLabel>{m.nav_settings()}</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>{visibleSettingsNavItems.map(renderNavItem)}</SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ) : null}
      </SidebarContent>
      <SidebarFooter>
        <SidebarNavUser
          userName={userName}
          userEmail={userEmail}
          userAvatar={userAvatar}
          onSignOut={onSignOut}
        />
      </SidebarFooter>
    </Sidebar>
  );
};
